<script>
/**
 * Carteira consolidada do cliente em foco: total aplicado, variação de 12 meses,
 * a alocação por classe e a curva de rentabilidade.
 *
 * ★ O valor e a variação delegam ao `IaInsightCard` na variante `hero` — um card
 * paralelo divergiria do painel do escritório no primeiro ajuste de tipografia,
 * e os dois vivem na MESMA coluna.
 *
 * ⚠️ Mas SEM os `segmentos` dele. A barra do `hero` desenha as fatias e para por
 * aí — com as três do AuC isso basta, porque a legenda cabe embaixo. Com as oito
 * ou nove classes de uma carteira real, a mesma barra vira decoração colorida
 * sem nome, e a legenda em texto ocupava mais altura que o gráfico. A alocação
 * ganhou componente próprio, com o nome da classe no hover.
 *
 * Componente BURRO: recebe ativos e série por prop, não chama API.
 */
import IaInsightCard from '@modules/aawz-intelligence-v2/components/ia-insight-card/ia-insight-card.vue'
import IaSparkline from '@modules/aawz-intelligence-v2/components/ia-insight-card/ia-sparkline.vue'
import { TONS } from '@modules/aawz-intelligence-v2/components/ia-insight-card/tons'
import {
  alocacaoPorClasse,
  totalAplicado,
} from '@modules/aawz-intelligence-v2/utils/cliente-em-foco/alocacao-por-classe'
import { abreviarMonetario, toPercent } from '@utils/Format'
import IaClienteAlocacao from './ia-cliente-alocacao.vue'

const ROTULO = 'Carteira consolidada'

// Esqueleto DESTA seção. O `IaInsightCard` tem o dele (136×30px), calibrado
// para o painel do escritório, onde o número é a manchete do bloco.
//
// ⚠️ As alturas espelham o conteúdo REAL, não são estéticas: 30px é a caixa do
// valor (26px × line-height 1.15) e 15px é a linha da classe sob a barra
// (11px × 1.4). Com números menores, o bloco crescia ao carregar e empurrava
// tarefas, contatos e o histórico para baixo — o pulo que incomodava.
const CAIXA_VALOR = { width: '104px', height: '30px' }
const CAIXA_CLASSE = { width: '132px', height: '15px' }

// Paleta da barra, na ordem das fatias: do verde da marca para os neutros, do
// maior pedaço para o menor. A lateral inteira é monocromática, e um arco-íris
// de oito cores aqui seria o único ponto colorido da coluna.
//
// ⚠️ Tokens, nunca hex: a plataforma é white-label.
const CORES = [
  'var(--success)',
  'var(--success-text)',
  'var(--primary)',
  'var(--neutral-700)',
  'var(--neutral-400)',
  'var(--neutral-300)',
]
// Quantas classes ganham pedaço próprio. O resto vira um "Outros" — abaixo da
// quinta fatia os pedaços ficam com menos de 3px e deixam de ser alvo clicável.
const CLASSES_NO_GRAFICO = 5
const ZERO = 0

export default {
  name: 'IaClienteCarteira',
  components: { IaInsightCard, IaSparkline, IaClienteAlocacao },
  props: {
    // Ativos do cliente, como o `ws/clientAssets` os entrega — JÁ filtrados
    // pelo cliente em foco.
    ativos: { type: Array, default: () => [] },
    // Rentabilidade dos últimos 12 meses. Vazia = sem cotização.
    serie: { type: Array, default: () => [] },
    // Último ponto da série, em pontos percentuais. `null` = não apurado.
    variacao: { type: Number, default: null },
    carregando: { type: Boolean, default: false },
    navegavel: { type: Boolean, default: false },
  },
  data() {
    return { CAIXA_VALOR, CAIXA_CLASSE }
  },
  computed: {
    rotulo() {
      return ROTULO
    },
    fatias() {
      return alocacaoPorClasse(this.ativos)
    },
    total() {
      return totalAplicado(this.ativos)
    },
    // Carteira sem cotização: o total zera e não há fatia. Mostrar "R$ 0,00"
    // afirmaria patrimônio zero, que é outra coisa — daí o traço.
    valor() {
      if (!this.total) return '—'
      return abreviarMonetario(this.total)
    },
    temVariacao() {
      return this.variacao !== null && isFinite(this.variacao)
    },
    destaque() {
      if (!this.temVariacao) return ''
      const sinal = this.variacao >= ZERO ? '+' : ''
      return sinal + toPercent(this.variacao)
    },
    tomDaVariacao() {
      if (!this.temVariacao) return TONS.NEUTRO
      return this.variacao >= ZERO ? TONS.POSITIVO : TONS.CRITICO
    },
    /**
     * Sem carteira cotizada o valor é um traço, e um traço sozinho não explica
     * nada — a legenda é quem diz que não é erro de carregamento.
     */
    legenda() {
      if (!this.total) return 'sem carteira cotizada'
      return this.temVariacao ? 'em 12 meses' : ''
    },
    /**
     * As maiores classes, mais "Outros" com o que sobrou.
     *
     * ⚠️ O resto é SOMADO, não descartado: sem ele a barra não fecharia a
     * largura e os percentuais não somariam 100.
     */
    fatiasDoGrafico() {
      const principais = this.fatias.slice(0, CLASSES_NO_GRAFICO)
      const resto = this.fatias.slice(CLASSES_NO_GRAFICO)
      const somaDoResto = resto.reduce((soma, f) => soma + f.valor, ZERO)

      const lista = principais.map((fatia, i) => ({
        chave: fatia.label,
        label: fatia.label,
        valor: fatia.valor,
        cor: CORES[Math.min(i, CORES.length - 1)],
      }))

      if (somaDoResto > ZERO) {
        lista.push({
          chave: '__outros',
          label: 'Outros (' + resto.length + ')',
          valor: somaDoResto,
          cor: CORES[CORES.length - 1],
        })
      }

      return lista
    },
    // Sem carteira não há manchete a dar: o traço em 26px ocupava a altura de um
    // número de milhões para dizer que não há número nenhum.
    semCarteira() {
      return !this.carregando && !this.total
    },
    temAlocacao() {
      return !this.carregando && this.fatiasDoGrafico.length > ZERO
    },
    /**
     * [feedback do usuário] Antes o carregando não desenhava curva nenhuma —
     * a decisão original era não prometer "montanha fantasma" pra cliente
     * sem carteira cotizada, que nunca teria curva de verdade. Revertido a
     * pedido: agora entra o MESMO fantasma que `ia-insight-card.vue` já usa
     * (`IaSparkline :fantasma`, não um retângulo) — mesmo risco que a barra
     * de alocação já corre (também pode não vir) e já era aceito ali.
     */
    temCurva() {
      return this.carregando || this.serie.length > ZERO
    },
  },
}
</script>

<template>
  <div
    class="ia-cliente-carteira"
    :class="{ 'ia-cliente-carteira--sem-carteira': semCarteira }"
  >
    <!-- Carregando NÃO passa pelo `IaInsightCard`: além do esqueleto grande
         demais, ele desenhava a barra de faixas dele — a que este painel deixou
         de usar —, e aparecia um gráfico antigo antes do atual. -->
    <template v-if="carregando">
      <span class="ia-cliente-carteira__rotulo ia-texto-selo">{{ rotulo }}</span>
      <BaseLoadingSkeleton v-bind="CAIXA_VALOR" />
      <span class="ia-cliente-carteira__barra-pendente" />
      <!-- A linha da classe sob a barra. Reservá-la é o que faz o bloco ter a
           MESMA altura antes e depois do dado — sem ela, o painel inteiro
           descia ~21px quando a carteira chegava. -->
      <BaseLoadingSkeleton v-bind="CAIXA_CLASSE" />
    </template>

    <!-- `navegavel` liga o chevron e o clique DENTRO do card — a mesma porta
         que os cards do painel de módulos usam. -->
    <IaInsightCard
      v-else
      variante="hero"
      :navegavel="navegavel"
      :rotulo="rotulo"
      :valor="valor"
      :destaque="destaque"
      :tom-destaque="tomDaVariacao"
      :legenda="legenda"
      @abrir="$emit('abrir')"
    />

    <IaClienteAlocacao v-if="temAlocacao" :fatias="fatiasDoGrafico" />

    <!-- [feedback do usuário] `:fantasma="carregando"` — mesmo gesto do
         `ia-insight-card.vue`: enquanto carrega, a MESMA curva desenhada em
         cinza com o pulso do esqueleto, não um retângulo genérico. -->
    <IaSparkline
      v-if="temCurva"
      class="ia-cliente-carteira__curva"
      :serie="serie"
      :fantasma="carregando"
    />
  </div>
</template>

<style lang="sass" scoped>
.ia-cliente-carteira
  display: flex
  flex-direction: column
  // 4px: o `IaInsightCard` já tem gap interno, e os dois somavam — sobrava um
  // vão do valor até a barra maior que o respiro entre blocos da coluna. A
  // barra pertence ao número, e o desenho tem de dizer isso pela distância.
  gap: 4px

// ⚠️ O `IaInsightCard` carrega a PRÓPRIA divisória e o próprio padding vertical
// — ele nasceu para se empilhar sozinho no painel de módulos, onde é a linha
// inteira. Dentro do painel do cliente quem desenha o ritmo é o bloco, e as
// duas regras somavam: duas divisórias com um vão morto no meio.
//
// Zerado aqui, e não no card: mexer lá mudaria o painel do escritório por causa
// desta tela.
::v-deep .ia-insight
  padding: 0
  border-bottom: none
  // O gap interno do card (rótulo → valor) é calibrado para o painel do
  // escritório, onde o card é o bloco inteiro. Aqui ele é a primeira de três
  // peças, e os 10px abriam demais entre "Carteira consolidada" e o número.
  gap: 4px


// O número de 26px carrega a entrelinha do corpo de texto, e ela vira um vão
// morto entre o valor e a barra. Colada ao glifo, a barra sobe ~8px.
::v-deep .ia-insight__valor--hero
  line-height: 1.15

// Sem carteira, o valor é um traço — e um traço em corpo de manchete ocupa a
// altura de "R$ 12,3 M" para dizer que não há valor. Cai para o corpo dos
// outros números do painel.
.ia-cliente-carteira--sem-carteira ::v-deep .ia-insight__valor--hero
  font-size: var(--ia-font-numero-destaque)

// Mesma régua do `.ia-insight__rotulo`. No carregando o rótulo é NOSSO — o card
// não chega a ser montado —, e sem esta regra ele saía como texto corrido do
// corpo da página, em caixa mista, no lugar do rótulo em versalete.
.ia-cliente-carteira__rotulo
  color: var(--neutral-600)
  letter-spacing: 0.06em
  text-transform: uppercase

// ★ 22px (era os 30px do componente) desde 14/09/2026 — parte do orçamento de
// 660px do painel (conta no `<style>` do `ia-cliente-painel.vue`). O traço não
// engrossa: o path leva `vector-effect="non-scaling-stroke"` (ver o
// `ia-sparkline.vue`). Dois seletores para vencer o `height: 30px` do próprio
// componente por especificidade — a mesma manobra do `.ia-insight__serie-grafico`.
.ia-cliente-carteira .ia-cliente-carteira__curva
  height: 22px

// Mesma caixa da barra real (8px), para a coluna não pular de altura na troca.
// `ia-shimmer` é a keyframe GLOBAL de `design/_ia-shimmer.sass`.
// ⚠️ 6px abaixo dela, o mesmo gap que a `IaClienteAlocacao` usa entre a barra e
// a linha da classe. Com o gap de 4px do contêiner, o esqueleto ficava 2px mais
// curto que o conteúdo — pouco, mas some com a promessa de altura igual.
.ia-cliente-carteira__barra-pendente
  margin-bottom: 2px
  height: 8px
  border-radius: 3px
  background: var(--neutral-300)
  animation: ia-shimmer 1.4s ease-in-out infinite

@media (prefers-reduced-motion: reduce)
  .ia-cliente-carteira__barra-pendente
    animation: none
</style>
